import { FC, useMemo, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { v4 as uuid } from 'uuid'
import { AutoComplete, Chip, TextField } from '@microservices/wiskey-react-components'
import { Check as CheckIcon, Clear as ClearIcon } from '@mui/icons-material'
import EditIcon from '@mui/icons-material/Edit'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  Paper,
  SxProps,
  Theme,
  Typography,
} from '@mui/material'
import { GridExpandMoreIcon } from '@mui/x-data-grid'

import { ColorSettings } from '@components/TextSettings/components/ColorSettings'

import { AutocompleteOption } from '@types'

type HeaderGroupParametersType = {
  options: AutocompleteOption[]
  containerSx?: SxProps<Theme>
  isExpanded: boolean
  onToggleExpanded: (expanded: boolean) => void
}

export const HeaderGroupParameters: FC<HeaderGroupParametersType> = ({
  containerSx,
  options,
  isExpanded,
  onToggleExpanded,
}) => {
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [groupName, setGroupName] = useState('')
  const [selectedColumns, setSelectedColumns] = useState<AutocompleteOption[]>([])
  const [groupColor, setGroupColor] = useState('none')
  const [originalValues, setOriginalValues] = useState<{
    groupName: string
    selectedColumns: AutocompleteOption[]
    groupColor: string
  } | null>(null)

  const LABEL_WIDTH = 120

  const methods = useFormContext()
  const { control, watch, setValue, getValues } = methods

  const watchedColumnGroup = watch('columnGroups')

  const usedColumnIds =
    watchedColumnGroup?.filter((_, idx) => idx !== editIndex).flatMap(group => group.children) || []

  const filteredOptions = options.filter(option => !usedColumnIds.includes(option.id))

  const { append, remove } = useFieldArray({
    name: 'columnGroups',
    control,
  })

  const isFormDirty = useMemo(() => {
    if (editIndex === null) {
      return Boolean(groupName || selectedColumns.length || groupColor !== 'none')
    }

    if (!originalValues) return false

    const nameChanged = groupName !== originalValues.groupName
    const colorChanged = groupColor !== originalValues.groupColor
    const columnsChanged =
      selectedColumns.length !== originalValues.selectedColumns.length ||
      selectedColumns.some((col, i) => col.id !== originalValues.selectedColumns[i]?.id)

    return nameChanged || colorChanged || columnsChanged
  }, [groupName, selectedColumns, groupColor, editIndex, originalValues])

  const isCheckDisabled = Boolean(!isFormDirty || !groupName || !selectedColumns.length)
  const isClearDisabled = Boolean(!groupName && !selectedColumns.length && groupColor === 'none')

  const handleAdd = () => {
    if (!groupName || !selectedColumns.length) return

    const newGroup = {
      code: uuid(),
      headerName: groupName,
      background: groupColor,
      children: selectedColumns.map(column => column.label),
    }

    newGroup.headerClassName = `headerGroup-${newGroup.code}`

    if (editIndex !== null) {
      setValue(
        'columnGroups',
        getValues('columnGroups').map((group, i) => (i === editIndex ? newGroup : group)),
        { shouldDirty: true }
      )
      setEditIndex(null)
    } else {
      append(newGroup)
    }

    setGroupName('')
    setSelectedColumns([])
    setGroupColor('none')
  }

  const handleClear = () => {
    setEditIndex(null)
    setGroupName('')
    setSelectedColumns([])
    setGroupColor('none')
    setOriginalValues(null)
  }

  const handleChangeColor = value => {
    setGroupColor(value)
  }

  const handleEdit = (group, index) => {
    const originalColumns = group.children.map(field => ({ id: field, label: field }))

    setEditIndex(index)
    setGroupName(group.headerName)
    setSelectedColumns(originalColumns)
    setGroupColor(group.background)

    setOriginalValues({
      groupName: group.headerName,
      selectedColumns: originalColumns,
      groupColor: group.background,
    })
  }

  return (
    <Grid
      container
      display='flex'
      flexWrap='nowrap'
      sx={{
        my: '3px',
        ...containerSx,
      }}
    >
      <Grid item alignItems='center'>
        <Typography color='#616161' paddingY={1.5} variant='body1' width={LABEL_WIDTH}>
          Groups
        </Typography>
      </Grid>
      <Grid flex={1} sx={{ overflow: 'hidden' }}>
        <Accordion
          expanded={isExpanded}
          sx={{
            width: '100%',
            flex: 1,
            '&.MuiPaper-root': {
              borderRadius: 0,
              border: '1px solid #00000042',
              boxShadow: 'none',
            },
          }}
          onChange={(_, expanded) => {
            onToggleExpanded(expanded)
            handleClear()
          }}
        >
          <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
            Header Group Parameters
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Grid
              container
              border='1px solid #fff'
              display={'flex'}
              flex={1}
              flexWrap='nowrap'
              gap={2}
              justifyContent={'space-between'}
              sx={{ scrollbarGutter: 'stable' }}
              width={'100%'}
            >
              <Grid container item gap={1} width={'100%'}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    placeholder='Group Name'
                    size='small'
                    value={groupName}
                    InputProps={{
                      sx: { fontSize: '18px' },
                    }}
                    onChange={e => setGroupName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <AutoComplete
                    disableCloseOnSelect
                    multiple
                    isOptionEqualToValue={(value, option) => value.id === option.id}
                    options={filteredOptions}
                    sx={{ width: '100%' }}
                    value={selectedColumns}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder='Group Columns'
                        size='small'
                        sx={{
                          minHeight: '24px',
                          '.MuiInputBase-root': {
                            fontSize: '18px',
                            '& .MuiChip-root': {
                              height: '24px',
                            },
                            '& .MuiChip-deleteIcon': {
                              fontSize: '18px',
                            },
                          },
                        }}
                      />
                    )}
                    onChange={newValue => setSelectedColumns(newValue || [])}
                  />
                </Grid>
                <ColorSettings
                  clearableColor
                  isEdit
                  labelNoWrap
                  color={typeof groupColor === 'string' ? groupColor.toLowerCase() : ''}
                  labelPlacement='top'
                  onChangeColor={handleChangeColor}
                />
              </Grid>
              <Grid item flex={1} sx={{ display: 'flex', justifyContent: 'center' }} xs={1.5}>
                <IconButton
                  disabled={isCheckDisabled}
                  size='medium'
                  sx={{ height: 40 }}
                  onClick={handleAdd}
                >
                  <CheckIcon />
                </IconButton>
                <IconButton
                  color='error'
                  disabled={isClearDisabled}
                  size='medium'
                  sx={{ height: 40 }}
                  onClick={handleClear}
                >
                  <ClearIcon />
                </IconButton>
              </Grid>
            </Grid>
            {!!watchedColumnGroup?.length && (
              <Paper
                variant='outlined'
                sx={{
                  mt: 2,
                  p: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  borderRadius: 1,
                }}
              >
                {watchedColumnGroup?.map((group, index) => (
                  <Chip
                    key={group.id}
                    variant='outlined'
                    label={
                      <Box
                        alignItems={'center'}
                        component='span'
                        display={'flex'}
                        gap={1}
                        justifyContent={'space-between'}
                      >
                        <Box>
                          <Typography component='span' fontWeight='500'>
                            {group.headerName}
                          </Typography>
                          {`: ${group.children.join(', ')}`}
                        </Box>
                        <Box display={'flex'}>
                          <IconButton
                            disabled={index === editIndex}
                            size='small'
                            sx={{
                              p: 2,
                              width: 24,
                              height: 24,
                            }}
                            onClick={() => handleEdit(group, index)}
                          >
                            <EditIcon fontSize='small' />
                          </IconButton>
                          <IconButton
                            disabled={editIndex !== null}
                            size='small'
                            sx={{
                              p: 2,
                              width: 24,
                              height: 24,
                            }}
                            onClick={() => remove(index)}
                          >
                            <ClearIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      </Box>
                    }
                    sx={{
                      bgcolor: group.background,
                      whiteSpace: 'normal',
                      height: 'auto',
                      width: '100%',
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        overflow: 'visible',
                        textOverflow: 'clip',
                        display: 'block',
                        lineHeight: 1.2,
                        textAlign: 'center',
                      },
                    }}
                  />
                ))}
              </Paper>
            )}
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  )
}
